import { SearchableSelect } from 'components';
import { TextField } from 'components/inputs/text-field';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useResponse } from 'contexts/response';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useTimeZone from 'hooks/use-timezone';
import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { viewInterchangePdf } from 'services/transactions';
import { TransactionRow } from 'types';
import './interchange-lookup.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const InterchangeLookup: FC = () => {
  const { newZonedDayJs, zonedStartOfDay } = useTimeZone();
  const { setError } = useResponse();

  useEffect(() => {
    fetch('/api/locations/names', { method: 'GET' })
      .then(response => response.json())
      .then((locs: string[]) => {
        if (Array.isArray(locs)) {
          const sorted = [...locs].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          setLocations(sorted);
        }
      });
  }, []);

  const [unitNumber, setUnitNumber] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [date, setDate] = useState<string>(zonedStartOfDay().format('YYYY-MM-DD'));
  const [scac, setScac] = useState<string>('');
  const [inbound, setInbound] = useState<string>('');

  const [locations, setLocations] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<TransactionRow[] | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'ALL' | 'INBOUND' | 'OUTBOUND'>('ALL');

  const gateInResults = useMemo(() => searchResults?.filter(tx => tx.inbound) ?? [], [searchResults]);
  const gateOutResults = useMemo(() => searchResults?.filter(tx => !tx.inbound) ?? [], [searchResults]);

  const availableTabs = useMemo(() => {
    const tabs: ('ALL' | 'INBOUND' | 'OUTBOUND')[] = [];
    if (gateInResults.length > 0 && gateOutResults.length > 0) {
      tabs.push('ALL', 'INBOUND', 'OUTBOUND');
    } else if (gateInResults.length > 0) {
      tabs.push('INBOUND');
    } else if (gateOutResults.length > 0) {
      tabs.push('OUTBOUND');
    }
    return tabs;
  }, [gateInResults.length, gateOutResults.length]);

  useEffect(() => {
    if (gateInResults.length > 0 && gateOutResults.length > 0) {
      setActiveTab('ALL');
    } else if (gateInResults.length > 0) {
      setActiveTab('INBOUND');
    } else if (gateOutResults.length > 0) {
      setActiveTab('OUTBOUND');
    }
  }, [searchResults, gateInResults.length, gateOutResults.length]);

  const filteredResults = useMemo(() => {
    if (activeTab === 'INBOUND') return gateInResults;
    if (activeTab === 'OUTBOUND') return gateOutResults;
    return searchResults ?? [];
  }, [activeTab, searchResults, gateInResults, gateOutResults]);

  const handleSearch = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSearching(true);
      setHasSearched(true);
      setSearchResults(null);

      const url = new URL('/api/transactions/interchanges/search', window.location.origin);
      url.searchParams.append('unitNumber', unitNumber.trim());
      if (location?.trim()) {
        url.searchParams.append('locationName', location.trim());
      }
      url.searchParams.append('date', date);
      url.searchParams.append('scac', scac.trim());
      if (inbound && inbound !== 'ALL') {
        url.searchParams.append('inbound', inbound);
      }

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: new Headers({ Accept: 'application/json' })
        });

        if (response.ok) {
          const results: TransactionRow[] = await response.json();
          setSearchResults(results);
          if (results.length === 0) {
            setError('No interchange found matching these criteria');
          }
        } else {
          setError('No interchange found');
          setSearchResults([]);
        }
      } catch (error) {
        console.error(error);
        setError('An error occurred while searching for interchanges');
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [unitNumber, location, date, scac, inbound, setError]
  );

  return (
    <div className="login-component">
      <div className="login-banner d-flex justify-content-center align-items-center h-100">
        <div className={`interchange-lookup-page page ${hasSearched && searchResults ? 'has-results' : ''}`}>
          <div className="page-header">
            <h1>Interchange Lookup</h1>
          </div>
          <div className="page-content">
            <form className="card-primary" onSubmit={handleSearch}>
              <div className="grid-3">
                <div className="field-div">
                  <input
                    className="flex-fill"
                    type="date"
                    value={date}
                    onChange={event => setDate(event.target.value ?? '')}
                    data-cy="date-input"
                    required
                  />
                </div>
                <div className="field-div">
                  <TextField label="Unit Number" value={unitNumber} onChange={setUnitNumber} required />
                </div>
                <div className="field-div">
                  <SearchableSelect
                    containerClass="text-field w-100"
                    label="Location (Optional)"
                    onValueSelected={setLocation}
                    options={locations.map(l => ({ label: l, value: l }))}
                    value={location}
                  />
                </div>
                <div className="field-div">
                  <TextField label="SCAC" value={scac} onChange={input => setScac(input.substring(0, 4))} required />
                </div>
                <div className="field-div">
                  <SearchableSelect
                    containerClass="text-field w-100"
                    label="In/Outbound (Optional)"
                    onValueSelected={setInbound}
                    options={[
                      { label: 'ALL', value: 'ALL' },
                      { label: 'INBOUND', value: 'true' },
                      { label: 'OUTBOUND', value: 'false' }
                    ]}
                    value={inbound}
                  />
                </div>
              </div>
              <div className="download-button-div">
                <button
                  className="btn-primary download-button"
                  disabled={!(!!unitNumber?.trim() && !!date && !!scac?.trim()) || searching}
                  type="submit"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>

            {hasSearched && searchResults && (
              <div className="card-primary p-3 interchange-results-card mt-4">
                <div className="card-header d-flex justify-content-between align-items-center mb-2">
                  <h4>SEARCH RESULTS ({searchResults.length})</h4>
                </div>

                {availableTabs.length > 1 && (
                  <div className="interchange-tabs d-flex g-2 mb-3">
                    {availableTabs.map((tab: 'ALL' | 'INBOUND' | 'OUTBOUND') => (
                      <button
                        key={tab}
                        type="button"
                        className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab === 'ALL' && `ALL (${searchResults.length})`}
                        {tab === 'INBOUND' && `GATE IN (${gateInResults.length})`}
                        {tab === 'OUTBOUND' && `GATE OUT (${gateOutResults.length})`}
                      </button>
                    ))}
                  </div>
                )}
                {availableTabs.length === 1 && (
                  <div className="interchange-tabs d-flex g-2 mb-3">
                    <span className="tab-btn active readonly">
                      {availableTabs[0] === 'INBOUND' && `GATE IN (${gateInResults.length})`}
                      {availableTabs[0] === 'OUTBOUND' && `GATE OUT (${gateOutResults.length})`}
                    </span>
                  </div>
                )}

                {searchResults.length === 0 ? (
                  <p className="text-muted p-3 text-center">No matching interchange transactions found.</p>
                ) : (
                  <div className="interchange-results-scroll-wrapper">
                    <div className="interchange-results-list d-flex flex-column g-3">
                      {filteredResults.map((tx: TransactionRow) => {
                        const units = [
                          tx.containerNumber,
                          tx.chassisNumber,
                          tx.gensetNumber,
                          tx.container?.unitNumber,
                          ...(tx.chassis ?? []).map(c => c.unitNumber),
                          ...(tx.gensets ?? []).map(g => g.unitNumber)
                        ].filter((num): num is string => Boolean(num && typeof num === 'string' && num.trim()));

                        const uniqueUnits = Array.from(new Set(units));
                        const unitDisplay = uniqueUnits.length > 0 ? uniqueUnits.join(' / ') : 'N/A';

                        return (
                          <div key={tx.id} className="interchange-result-item p-3 border rounded shadow-sm">
                            <div className="result-header d-flex align-items-center justify-content-between flex-wrap mb-2">
                              <span className={`result-badge ${tx.inbound ? 'inbound' : 'outbound'}`}>
                                {tx.inbound ? 'GATE IN' : 'GATE OUT'}
                              </span>
                              <span className="result-date">
                                {tx.completedDate
                                  ? tx.location?.timeZone
                                    ? dayjs.utc(tx.completedDate).tz(tx.location.timeZone).format('M/D/YYYY h:mm A z')
                                    : newZonedDayJs(tx.completedDate).format('M/D/YYYY h:mm A')
                                  : ''}
                              </span>
                            </div>

                            <div className="result-details-grid mb-3">
                              <div className="detail-item">
                                <span className="detail-label">Location</span>
                                <span className="detail-value">{tx.location?.name ?? 'N/A'}</span>
                              </div>
                              {tx.containerNumber ? (
                                <div className="detail-item">
                                  <span className="detail-label">Container</span>
                                  <span className="detail-value">{tx.containerNumber}</span>
                                </div>
                              ) : null}
                              {tx.chassisNumber ? (
                                <div className="detail-item">
                                  <span className="detail-label">Chassis</span>
                                  <span className="detail-value">{tx.chassisNumber}</span>
                                </div>
                              ) : null}
                              {tx.gensetNumber ? (
                                <div className="detail-item">
                                  <span className="detail-label">Genset</span>
                                  <span className="detail-value">{tx.gensetNumber}</span>
                                </div>
                              ) : null}
                              {!tx.containerNumber && !tx.chassisNumber && !tx.gensetNumber && (
                                <div className="detail-item">
                                  <span className="detail-label">Unit</span>
                                  <span className="detail-value">{unitDisplay}</span>
                                </div>
                              )}
                              <div className="detail-item">
                                <span className="detail-label">Carrier</span>
                                <span className="detail-value">{tx.truckingCompany?.name ?? 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">SCAC</span>
                                <span className="detail-value">
                                  {tx.truckingCompany?.scacCode ?? tx.scacCode ?? 'N/A'}
                                </span>
                              </div>
                            </div>

                            <div className="result-action-div">
                              <button
                                className="btn-primary pdf-download-btn w-100 d-flex align-items-center justify-content-center"
                                onClick={() => viewInterchangePdf(tx.id)}
                              >
                                VIEW INTERCHANGE
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ErrorDialog />
    </div>
  );
};

export default InterchangeLookup;
